import React, { useState, useEffect } from "react";
import api from "../services/api";
import "./FinanzasPage.css";

interface MiCuentaPageProps {
  clientId?: number; // ID específico de cliente (para admin grupos)
}

export default function MiCuentaPage({ clientId }: MiCuentaPageProps) {
  const [cuentaData, setCuentaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("");

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const loadMiCuenta = async () => {
    setLoading(true);
    setError("");
    
    try {
      let response;
      if (clientId) {
        // Si se especifica un clientId, cargar datos de ese cliente específico
        response = await api.get(`/clients/${clientId}/account-statement`);
      } else {
        // Comportamiento normal - cargar datos del cliente actual
        response = await api.get("/finanzas/mi-cuenta");
      }
      setCuentaData(response.data);
    } catch (err) {
      setError(`Error cargando datos: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMiCuenta();
  }, [clientId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>⏳ Cargando Mi Estado de Cuenta...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>❌ Error</h1>
        </div>
        <div className="alert alert-error">
          {error}
          <button 
            onClick={() => setError("")}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              float: 'right',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: '10px'
            }}
            title="Cerrar mensaje"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  if (!cuentaData || !cuentaData.usuario) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>📊 Mi Estado de Cuenta</h1>
        </div>
        <div className="alert alert-error">
          No se pudieron cargar los datos de su cuenta.
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              float: 'right',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: '10px'
            }}
            title="Reintentar"
          >
            🔄
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          📊 Mi Estado de Cuenta - {cuentaData.usuario?.nombre || 'Usuario'}
        </h1>
        <div className="header-actions">
          <button
            onClick={loadMiCuenta}
            className="btn btn-secondary"
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Información del Usuario */}
      <div className="section">
        <h2>👤 Información Personal</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Nombre</h3>
            <p className="stat-value">{cuentaData.usuario?.nombre || 'N/A'}</p>
            <small>Email: {cuentaData.usuario?.email || 'N/A'}</small>
          </div>
        </div>
      </div>

      {/* Dashboard para Clientes */}
      {cuentaData.tipoUsuario === 'cliente' && cuentaData.cliente && (
        <>
          {/* Información del Cliente */}
          <div className="section">
            <h2>🏢 Información de Cliente</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Empresa</h3>
                <p className="stat-value">{cuentaData.cliente?.nombre || 'N/A'}</p>
                <small>CUIT: {cuentaData.cliente?.cuit || 'N/A'}</small>
              </div>
              <div className="stat-card">
                <h3>Costo por Auditoría</h3>
                <p className="stat-value">{formatMoney(cuentaData.cliente?.costoAuditoria || 0)}</p>
              </div>
            </div>
          </div>

          {/* Estado de Cuenta del Cliente */}
          <div className="section">
            <h2>💰 Estado de Cuenta</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Facturado</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta?.deudaTotal || 0)}</p>
                <small>{cuentaData.estadoCuenta?.auditoriasCompletadas || 0} auditorías completadas</small>
              </div>
              <div className="stat-card positive">
                <h3>Total Pagado</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta?.totalPagado || 0)}</p>
              </div>
              <div className={`stat-card ${(cuentaData.estadoCuenta?.saldoPendiente || 0) > 0 ? 'negative' : 'positive'}`}>
                <h3>Total Pendiente</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta?.saldoPendiente || 0)}</p>
                <small>Estado: {cuentaData.estadoCuenta?.estadoDeuda?.toUpperCase() || 'N/A'}</small>
              </div>
              <div className="stat-card negative">
                <h3>Pendientes de Pago</h3>
                <p className="stat-value">
                  {cuentaData.auditorias?.completadas?.filter(audit =>
                    audit.estadoPago === 'PENDIENTE' || audit.estadoPago === 'FACTURADA'
                  ).length || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Auditorías Completadas */}
          {cuentaData.auditorias?.completadas?.length > 0 && (
            <div className="section">
              <h2>✅ Auditorías Completadas</h2>

              {/* Filtro por fecha */}
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  placeholder="Buscar por fecha (ej: 25/9/2025)"
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    width: '250px'
                  }}
                />
              </div>

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Auditoría</th>
                      <th>Fecha</th>
                      <th>Valor</th>
                      <th>Estado Pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentaData.auditorias?.completadas
                      ?.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()) // Más nuevas arriba
                      ?.filter(auditoria => {
                        if (!filtroFecha) return true;
                        const fechaFormateada = formatDate(auditoria.fecha);
                        return fechaFormateada.includes(filtroFecha);
                      })
                      ?.map(auditoria => (
                      <tr key={auditoria.id}>
                        <td style={{ fontWeight: '600' }}>
                          {auditoria.titulo && auditoria.titulo !== `Auditoría #${auditoria.id}` && auditoria.titulo.trim() !== ''
                            ? auditoria.titulo
                            : `Auditoría #${auditoria.id}`
                          }
                        </td>
                        <td>{formatDate(auditoria.fecha)}</td>
                        <td className="positive">{formatMoney(auditoria.valor)}</td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            backgroundColor: auditoria.estadoPago === 'PAGADA' ? '#d1fae5' : '#fee2e2',
                            color: auditoria.estadoPago === 'PAGADA' ? '#065f46' : '#991b1b',
                            border: auditoria.estadoPago === 'PAGADA' ? '1px solid #10b981' : '1px solid #ef4444'
                          }}>
                            {auditoria.estadoPago === 'PAGADA' ? 'PAGADO' : 'PENDIENTE'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Últimos Pagos */}
          {cuentaData.ultimosPagos?.length > 0 && (
            <div className="section">
              <h2>💳 Últimos Pagos</h2>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Monto</th>
                      <th>Concepto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentaData.ultimosPagos?.map((pago, index) => (
                      <tr key={index}>
                        <td>{formatDate(pago.fecha)}</td>
                        <td className="negative">{formatMoney(pago.monto)}</td>
                        <td>{pago.concepto}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dashboard para Operadores */}
      {cuentaData.tipoUsuario === 'operador' && (
        <>
          {/* Estado de Cuenta del Operador */}
          <div className="section">
            <h2>💰 Estado de Cuenta - Comisiones</h2>
            <div className="stats-grid">
              <div className="stat-card positive">
                <h3>Comisiones Devengadas</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta.comisionesDevengadas)}</p>
                <small>{cuentaData.estadoCuenta.auditoriasCompletadas} auditorías completadas</small>
              </div>
              <div className="stat-card negative">
                <h3>Total Pagado</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta.totalPagado)}</p>
              </div>
              <div className={`stat-card ${cuentaData.estadoCuenta.saldoPendiente > 0 ? 'positive' : 'text-center'}`}>
                <h3>Saldo Pendiente</h3>
                <p className="stat-value">{formatMoney(cuentaData.estadoCuenta.saldoPendiente)}</p>
                <small>Estado: {cuentaData.estadoCuenta?.estadoCuenta?.toUpperCase() || 'N/A'}</small>
              </div>
              <div className="stat-card">
                <h3>Auditorías Pendientes</h3>
                <p className="stat-value">{cuentaData.estadoCuenta.auditoriasPendientes}</p>
              </div>
            </div>
          </div>

          {/* Contador de Auditorías por Operador */}
          <div className="section">
            <h2>📊 Resumen de Auditorías</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Auditorías</h3>
                <p className="stat-value">{cuentaData.estadoCuenta.totalAuditorias}</p>
              </div>
              <div className="stat-card positive">
                <h3>Completadas</h3>
                <p className="stat-value">{cuentaData.estadoCuenta.auditoriasCompletadas}</p>
              </div>
              <div className="stat-card">
                <h3>Pendientes</h3>
                <p className="stat-value">{cuentaData.estadoCuenta.auditoriasPendientes}</p>
              </div>
              <div className="stat-card">
                <h3>Promedio Comisión</h3>
                <p className="stat-value">
                  {cuentaData.estadoCuenta.auditoriasCompletadas > 0 
                    ? formatMoney(cuentaData.estadoCuenta.comisionesDevengadas / cuentaData.estadoCuenta.auditoriasCompletadas)
                    : formatMoney(0)
                  }
                </p>
                <small>por auditoría</small>
              </div>
            </div>
          </div>

          {/* Auditorías Completadas */}
          {cuentaData.auditorias?.completadas?.length > 0 && (
            <div className="section">
              <h2>✅ Auditorías Completadas</h2>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Valor Base</th>
                      <th>Comisión (15%)</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentaData.auditorias?.completadas?.map(auditoria => (
                      <tr key={auditoria.id}>
                        <td>{formatDate(auditoria.fecha)}</td>
                        <td>{auditoria.cliente}</td>
                        <td>{formatMoney(auditoria.valorBase)}</td>
                        <td className="positive">{formatMoney(auditoria.comision)}</td>
                        <td>
                          <span className="tipo-badge positive">COMPLETADA</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Auditorías Pendientes */}
          {cuentaData.auditorias?.pendientes?.length > 0 && (
            <div className="section">
              <h2>⏳ Auditorías Pendientes</h2>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha Programada</th>
                      <th>Cliente</th>
                      <th>Valor Base</th>
                      <th>Comisión Estimada</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentaData.auditorias?.pendientes?.map(auditoria => (
                      <tr key={auditoria.id}>
                        <td>{formatDate(auditoria.fecha)}</td>
                        <td>{auditoria.cliente}</td>
                        <td>{formatMoney(auditoria.valorBase)}</td>
                        <td className="text-center">{formatMoney(auditoria.valorBase * 0.15)}</td>
                        <td>
                          <span className="tipo-badge negative">{auditoria.estado?.toUpperCase() || 'N/A'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Últimos Pagos */}
          {cuentaData.ultimosPagos?.length > 0 && (
            <div className="section">
              <h2>💳 Últimos Pagos de Sueldo</h2>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Monto</th>
                      <th>Concepto</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentaData.ultimosPagos?.map((pago, index) => (
                      <tr key={index}>
                        <td>{formatDate(pago.fecha)}</td>
                        <td className="positive">{formatMoney(pago.monto)}</td>
                        <td>{pago.concepto}</td>
                        <td>
                          <span className={`tipo-badge ${pago.esParcial ? 'negative' : 'positive'}`}>
                            {pago.esParcial ? 'PARCIAL' : 'TOTAL'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Para otros tipos de usuario */}
      {cuentaData.tipoUsuario === 'otro' && (
        <div className="section">
          <div className="alert alert-error">
            {cuentaData.mensaje}
            <button 
              onClick={() => window.history.back()}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                float: 'right',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '0',
                marginLeft: '10px'
              }}
              title="Volver"
            >
              ← 
            </button>
          </div>
        </div>
      )}
    </div>
  );
}